import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Building2, Plus, Crown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INST_TYPES = ["College", "University", "School", "Company", "Coaching Institute", "Organization", "Other"];

const Stat = ({ label, value }) => (
  <div className="t-card p-4">
    <p className="font-display font-black text-2xl">{value}</p>
    <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mt-1">{label}</p>
  </div>
);

export default function SuperAdmin() {
  const [overview, setOverview] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "College", description: "", domains: "" });

  const load = useCallback(() => {
    api.get("/super/overview").then(({ data }) => setOverview(data)).catch((e) => toast.error(apiError(e)));
    api.get("/super/institutions").then(({ data }) => setInstitutions(data)).catch(() => {});
    api.get("/super/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/super/reports").then(({ data }) => setReports(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const createInst = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      await api.post("/super/institutions", {
        name: form.name, type: form.type, description: form.description,
        email_domains: form.domains.split(",").map((d) => d.trim()).filter(Boolean),
      });
      toast.success("Institution created");
      setCreateOpen(false);
      setForm({ name: "", type: "College", description: "", domains: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!overview) return <p className="text-muted-foreground animate-pulse text-center py-10">Loading platform…</p>;

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <h1 className="font-display font-black text-3xl flex items-center gap-2"><Crown className="w-7 h-7 text-primary" /> THIRI Super Admin</h1>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Institutions" value={overview.institutions} />
        <Stat label="Members" value={overview.users} />
        <Stat label="Rides" value={overview.rides} />
        <Stat label="Open reports" value={overview.open_reports} />
      </div>

      <Tabs defaultValue="institutions">
        <TabsList className="rounded-full">
          <TabsTrigger data-testid="super-tab-institutions" value="institutions" className="rounded-full">Institutions</TabsTrigger>
          <TabsTrigger data-testid="super-tab-users" value="users" className="rounded-full">Users</TabsTrigger>
          <TabsTrigger data-testid="super-tab-reports" value="reports" className="rounded-full">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="institutions" className="mt-6 space-y-3">
          <Button data-testid="create-institution-btn" onClick={() => setCreateOpen(true)} className="rounded-full font-bold">
            <Plus className="w-4 h-4 mr-2" /> Add institution
          </Button>
          {institutions.map((i) => (
            <div key={i.id} className="t-card p-5 flex items-center gap-4" data-testid={`institution-${i.id}`}>
              <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-primary" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-bold">{i.name} <span className="text-xs font-normal text-muted-foreground">· {i.type}</span></p>
                <p className="text-xs text-muted-foreground">{i.member_count} members · domains: {i.email_domains?.join(", ") || "none"}</p>
              </div>
              <span className="t-chip bg-secondary/20 text-[#14532D]">{i.status}</span>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="t-card divide-y divide-black/5">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`super-user-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                </div>
                <span className="t-chip bg-black/5">{u.role.replace("_", " ")}</span>
                <span className={`t-chip ${u.verification_status === "verified" ? "bg-secondary/20 text-[#14532D]" : "bg-[#F9C31F] text-[#12211C]"}`}>{u.verification_status}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-3">
          {reports.length ? reports.map((r) => (
            <div key={r.id} className="t-card p-4" data-testid={`super-report-${r.id}`}>
              <p className="text-sm font-bold">{r.category.replace("_", " ")} <span className="font-normal text-muted-foreground">on {r.target_type}</span></p>
              <p className="text-xs text-muted-foreground mt-1">{r.details || "No details"} · status: {r.status}</p>
            </div>
          )) : <div className="t-card p-8 text-center text-sm text-muted-foreground" data-testid="no-super-reports">No reports across the platform.</div>}
        </TabsContent>
      </Tabs>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>New institution</DialogTitle></DialogHeader>
          <Input data-testid="inst-name-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-2xl" />
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="inst-type-select" className="rounded-2xl"><SelectValue /></SelectTrigger>
            <SelectContent>{INST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="inst-description-input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl" />
          <Input data-testid="inst-domains-input" placeholder="Official email domains (comma separated)" value={form.domains} onChange={(e) => setForm({ ...form, domains: e.target.value })} className="rounded-2xl" />
          <Button data-testid="inst-submit-btn" onClick={createInst} className="rounded-full font-bold">Create</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
